import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class InactivityService {
    private readonly INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hora en milisegundos
    private inactivityTimer: any;
    private lastActivityTime: number;
    private destroy$ = new Subject<void>();
    private isMonitoring = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private ngZone: NgZone
    ) {
        this.lastActivityTime = Date.now();
    }

    /**
     * Inicia el monitoreo de inactividad
     */
    startMonitoring(): void {
        if (this.isMonitoring) {
            return;
        }

        this.isMonitoring = true;
        this.lastActivityTime = Date.now();

        // Eventos de actividad del usuario
        const userActivity$ = merge(
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'click')
        );

        // Escuchar eventos de actividad del usuario (con debounce para optimizar)
        this.ngZone.runOutsideAngular(() => {
            userActivity$
                .pipe(
                    debounceTime(1000), // Evitar múltiples actualizaciones por segundo
                    takeUntil(this.destroy$)
                )
                .subscribe(() => {
                    this.ngZone.run(() => {
                        this.resetTimer();
                    });
                });
        });

        // Iniciar el timer de inactividad
        this.resetTimer();

        const timeoutMinutes = this.INACTIVITY_TIMEOUT / 60000;
    }

    /**
     * Detiene el monitoreo de inactividad
     */
    stopMonitoring(): void {
        this.isMonitoring = false;
        this.clearTimer();
        this.destroy$.next();
    }

    /**
     * Reinicia el timer de inactividad
     * Este método es llamado automáticamente cuando:
     * - El usuario interactúa con la interfaz (clicks, teclas, scroll)
     * - Se hace una petición HTTP a cualquier backend
     */
    resetTimer(): void {
        const now = Date.now();
        const timeSinceLastActivity = now - this.lastActivityTime;

        // Solo reiniciar si ha pasado más de 1 segundo desde la última actividad
        if (timeSinceLastActivity < 1000) {
            return;
        }

        this.lastActivityTime = now;
        this.clearTimer();

        // Crear nuevo timer
        this.inactivityTimer = setTimeout(() => {
            this.handleInactivity();
        }, this.INACTIVITY_TIMEOUT);
    }

    /**
     * Notifica actividad (llamado desde el interceptor HTTP)
     */
    notifyActivity(): void {
        if (this.isMonitoring) {
            this.resetTimer();
        }
    }

    /**
     * Maneja el evento de inactividad
     */
    private handleInactivity(): void {
        console.warn('[Inactivity] SESION EXPIRADA - Usuario inactivo, cerrando sesion...');

        // Limpiar el monitoreo
        this.stopMonitoring();

        // Cerrar sesión de forma silenciosa (sin popup de Microsoft)
        this.authService.signOutSilent().subscribe({
            next: () => {
                // Redirigir al login con mensaje
                this.router.navigate(['/sign-in'], {
                    queryParams: {
                        sessionExpired: 'true',
                        reason: 'inactivity'
                    }
                });
            },
            error: (error) => {
                console.error('[Inactivity] Error al cerrar sesion:', error);
                // Forzar redirección aunque haya error
                this.router.navigate(['/sign-in'], {
                    queryParams: {
                        sessionExpired: 'true',
                        reason: 'inactivity'
                    }
                });
            }
        });
    }

    /**
     * Limpia el timer actual
     */
    private clearTimer(): void {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    /**
     * Obtiene el tiempo restante antes del logout (en minutos)
     */
    getRemainingTime(): number {
        const elapsed = Date.now() - this.lastActivityTime;
        const remaining = this.INACTIVITY_TIMEOUT - elapsed;
        return Math.max(0, Math.floor(remaining / 60000)); // Convertir a minutos
    }

    /**
     * Destructor del servicio
     */
    ngOnDestroy(): void {
        this.stopMonitoring();
        this.destroy$.complete();
    }
}
